import { loadStripe, Stripe } from '@stripe/stripe-js'
import { supabase } from './supabase'

// Check if Stripe is configured
const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
export const isStripeConfigured = !!publishableKey

// Singleton pattern - only load Stripe once
let stripePromiseInstance: Promise<Stripe | null> | null = null

const getStripePromise = (): Promise<Stripe | null> => {
  if (!stripePromiseInstance) {
    if (!publishableKey) {
      if (import.meta.env.DEV) {
        console.error('Missing VITE_STRIPE_PUBLISHABLE_KEY environment variable')
      }
      return Promise.resolve(null)
    }
    try {
      stripePromiseInstance = loadStripe(publishableKey)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to load Stripe:', error)
      }
      return Promise.resolve(null)
    }
  }
  return stripePromiseInstance
}

// Export both the promise (for Elements provider) and the getter function
export const stripePromise = getStripePromise()
export const getStripe = getStripePromise

interface CreatePaymentIntentParams {
  amount: number // Amount in cents
  currency?: string
  metadata?: Record<string, string>
}

interface PaymentIntentResponse {
  clientSecret: string
  paymentIntentId: string
}

/**
 * Creates a payment intent via the secure backend endpoint.
 * The secret key is NEVER exposed to the frontend.
 */
export const createPaymentIntent = async ({
  amount,
  currency = 'usd',
  metadata = {},
}: CreatePaymentIntentParams): Promise<PaymentIntentResponse> => {
  const { data, error } = await supabase.functions.invoke<PaymentIntentResponse>(
    'create-payment-intent',
    {
      body: { amount, currency, metadata },
    }
  )

  if (error) {
    throw new Error(error.message || 'Failed to create payment intent')
  }

  if (!data) {
    throw new Error('No data returned from payment intent creation')
  }

  return data
}
